/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { SimplicityImage } from "cms/images/simplicity-image";
import { SVGIcons } from "cms/icons/svg-icons";
import Config from "data/config";
import { isGuid } from "utils/empty-exists";

const CardDisplay = ({ card }) => {
    const { token } = useAuth();
    const { type, title, description, banner_image, webpage, link, document } = card || {};
    
    const [webpageData, setWebpageData] = useState(null);
    const [linkData, setLinkData] = useState(null);
    const [documentData, setDocumentData] = useState(null);
    const [targetUrl, setTargetUrl] = useState(null);
    
    // Fetch webpage, link, or document based on type
    useEffect(() => {
        const fetchData = async () => {
            if (type === "webpage" && webpage && isGuid(webpage)) {
                try {
                    const webpageResult = await SimpliCityAdminSDK.webpage.findOne(token, webpage);
                    setWebpageData(webpageResult);
                    if (webpageResult?.path) {
                        setTargetUrl(`${Config.previewUrl}${webpageResult.path}`);
                    }
                } catch (error) {
                    console.error("Error fetching webpage:", error);
                }
            } else if (type === "link" && link && isGuid(link)) {
                try {
                    const linkResult = await SimpliCityAdminSDK.link.findOne(token, link);
                    setLinkData(linkResult);
                    if (linkResult?.url) {
                        setTargetUrl(linkResult.url);
                    }
                } catch (error) {
                    console.error("Error fetching link:", error);
                }
            } else if (type === "document" && document && isGuid(document)) {
                try {
                    const documentResult = await SimpliCityAdminSDK.document.findOne(token, document);
                    setDocumentData(documentResult);
                    if (documentResult?.url) {
                        setTargetUrl(documentResult.url);
                    }
                } catch (error) {
                    console.error("Error fetching document:", error);
                }
            }
        };
        
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, webpage, link, document, token]);
    
    // Get image GUID and settings from banner_image
    const imageGuid = banner_image?.image;
    const imageSettings = banner_image?.settings;
    
    return (
        <div className="simplicity-card-display">
            {banner_image && imageGuid && (
                <div className="simplicity-card-display-image">
                    <SimplicityImage image={imageGuid} settings={imageSettings} />
                </div>
            )}
            
            <div className="simplicity-card-display-content">
                {title && (
                    <div className="simplicity-card-display-title">
                        {targetUrl ? (
                            <a href={targetUrl} target="_blank" rel="noopener noreferrer" className="simplicity-card-display-title-link">
                                {title}
                                {type === "document" && (
                                    <span className="simplicity-card-display-title-link-icon">
                                        <SVGIcons.common.ExternalLink />
                                    </span>
                                )}
                            </a>
                        ) : (
                            <span>{title}</span>
                        )}
                    </div>
                )}
                
                {description && (
                    <div className="simplicity-card-display-description">
                        {description}
                    </div>
                )}
            </div>
        </div>
    );
};

CardDisplay.displayName = "CardDisplay";
export { CardDisplay };
